import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import Yasgui from '@triply/yasgui'
import '@triply/yasgui/build/yasgui.min.css'
import { getCgiUrl } from '../helpers/url'

interface YasguiEditorProps {
  repoURL: string
  repoName: string
  localRepoURL?: string
  id: string
  initialQuery?: string
}

export interface YasguiEditorHandle {
  getCurrentQuery: () => string | undefined
}

type PersistedTab = { name: string; query: string }

function YasguiOverrideStyles() {
  return (
    <style>{`
      .yasgui .controlbar .tabContextButton { display: none !important; }
      .yasgui .controlbar .autocompleteWrapper { display: none !important; }
    `}</style>
  )
}

// ─── Persistence ──────────────────────────────────────────────────────────────

const PERSISTENCE_KEY_PREFIX = 'yasgui-queries-'

function loadPersistedTabs(editorId: string): PersistedTab[] {
  try {
    const stored = localStorage.getItem(PERSISTENCE_KEY_PREFIX + editorId)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function saveTabsToStorage(editorId: string, yasgui: Yasgui) {
  try {
    // NOTE: Yasgui does not expose a public API for iterating all tabs,
    // so we reach into private internals here intentionally.
    const allTabs: Record<string, any> = (yasgui as any)._tabs ?? (yasgui as any).tabs
    if (!allTabs) return

    const tabOrder: string[] = (yasgui as any).persistentConfig?.tabOrder
      ?? (yasgui as any)._tabOrder
      ?? Object.keys(allTabs)

    const tabsToSave: PersistedTab[] = tabOrder
      .map(tabId => allTabs[tabId])
      .filter(Boolean)
      .map(tab => ({
        name: tab.persistentConfig?.name ?? tab.name ?? 'Query',
        query: tab.yasqe?.getValue?.() ?? '',
      }))

    localStorage.setItem(PERSISTENCE_KEY_PREFIX + editorId, JSON.stringify(tabsToSave))
  } catch {
    // ignore — best effort persistence
  }
}

// ─── Yasgui setup ─────────────────────────────────────────────────────────────

function createYasgui(container: HTMLElement, endpoint: string): Yasgui {
  return new Yasgui(container, {
    requestConfig: {
      endpoint,
      method: 'POST',
      withCredentials: true,
    },
    copyEndpointOnNewTab: false,
    // Disable Yasgui's own persistence — we manage it ourselves per-instance
    // to avoid multiple editors overwriting each other's localStorage entries.
    persistenceId: null,
  } as any)
}

function restorePersistedTabs(yasgui: Yasgui, persistedTabs: PersistedTab[], endpoint: string) {
  try { (yasgui.getTab() as any)?.close() } catch { /* ignore if no default tab */ }

  persistedTabs.forEach(({ name, query }, index) => {
    const isFirstTab = index === 0
    try {
      const tab = yasgui.addTab(isFirstTab, {
        ...((Yasgui as any).Tab?.getDefaults?.() ?? {}),
        name,
        requestConfig: { endpoint, method: 'POST', withCredentials: true },
      } as any) as any
      tab?.yasqe?.setValue(query)
    } catch (e) {
      console.warn('Failed to restore tab', name, e)
    }
  })
}

function setInitialQuery(yasgui: Yasgui, query: string) {
  const tab = yasgui.getTab() as any
  tab?.yasqe?.setValue(query)
}

// ─── Component ────────────────────────────────────────────────────────────────

export const YasguiEditor = forwardRef<YasguiEditorHandle, YasguiEditorProps>(
  function YasguiEditor({ repoURL, repoName, localRepoURL, id, initialQuery }, ref) {
    const containerRef = useRef<HTMLDivElement>(null)
    const yasguiRef = useRef<Yasgui | null>(null)

    const endpoint = React.useMemo(
      () => getCgiUrl('sparql', repoName, repoURL, localRepoURL),
      [repoName, repoURL, localRepoURL]
    )

    useImperativeHandle(ref, () => ({
      getCurrentQuery: () => {
        const tab = yasguiRef.current?.getTab() as any
        return tab?.yasqe?.getValue()
      }
    }), [])

    useEffect(() => {
      if (!containerRef.current) return

      const persistedTabs = loadPersistedTabs(id)
      const yasgui = createYasgui(containerRef.current, endpoint)

      if (initialQuery) {
        setInitialQuery(yasgui, initialQuery)
      } else if (persistedTabs.length > 0) {
        restorePersistedTabs(yasgui, persistedTabs, endpoint)
      }

      yasguiRef.current = yasgui

      const persistenceInterval = setInterval(() => saveTabsToStorage(id, yasgui), 3000)

      return () => {
        clearInterval(persistenceInterval)
        saveTabsToStorage(id, yasgui)
        yasgui.destroy()
        yasguiRef.current = null
      }
    }, [endpoint, id])

    return (
      <>
        <YasguiOverrideStyles />
        <div ref={containerRef} id={id} style={{ height: '100%', width: '100%' }} />
      </>
    )
  }
)